import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  ListItemText,
  useMediaQuery,
  useTheme as useMuiTheme
} from '@mui/material'
import {
  Menu as MenuIcon,
  AccountCircle,
  LightMode,
  DarkMode
} from '@mui/icons-material'
import { useTheme } from '../../contexts/ThemeContext'
import {
  selectUserIsAuthenticated,
  selectUserDisplayName,
  selectUserPhotoURL,
  handleLogout,
  nukeUserAccount,
  repairUserAccount,
} from '../../store/slices/userSlice'

const MainAppHeader = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isDarkMode, toggleDarkMode } = useTheme()
  const muiTheme = useMuiTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'))
  const isAuthenticated = useSelector(selectUserIsAuthenticated)
  const userDisplayName = useSelector(selectUserDisplayName)
  const userPhotoURL = useSelector(selectUserPhotoURL)
  const [anchorEl, setAnchorEl] = useState(null)

  const handleLogoutClick = async () => {
    await dispatch(handleLogout())
    setAnchorEl(null)
  }

  const handleNukeAccount = async () => {
    await dispatch(nukeUserAccount())
    setAnchorEl(null)
  }

  const handleRepairAccount = async () => {
    await dispatch(repairUserAccount())
    setAnchorEl(null)
  }

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleTitleClick = () => {
    if (isAuthenticated) {
      navigate('/flock-manager')
    } else {
      navigate('/')
    }
  }

  return (
    <AppBar position="static" elevation={1}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            edge="start"
            color="inherit"
            size="large"
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="div"
            onClick={handleTitleClick}
            sx={{ 
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            Origin Organizer
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            color="inherit"
            onClick={toggleDarkMode}
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            size="large"
          >
            {isDarkMode ? <LightMode /> : <DarkMode />}
          </IconButton>
          
          {isAuthenticated && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isMobile && (
                <Typography variant="body2" color="inherit">
                  {userDisplayName}
                </Typography>
              )}
              
              <IconButton
                color="inherit"
                onClick={handleMenuOpen}
                size="large"
              >
                {userPhotoURL ? (
                  <Avatar src={userPhotoURL} sx={{ width: 24, height: 24 }} />
                ) : (
                  <AccountCircle />
                )}
              </IconButton>
              
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem disabled>
                  <ListItemText primary={userDisplayName} />
                </MenuItem>
                <MenuItem onClick={handleRepairAccount}>
                  <ListItemText primary="Repair Account" />
                </MenuItem>
                <MenuItem onClick={handleNukeAccount} sx={{ color: 'error.main' }}>
                  <ListItemText primary="Delete Account" />
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogoutClick}>
                  <ListItemText primary="Logout" />
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default MainAppHeader